import { differenceInDays } from 'date-fns';
import * as Cookies from 'js-cookie';
import pick from 'lodash-es/pick';
import * as moment from 'moment';
import { createSelector } from 'reselect';

import type APIAddress from '~/types/APIAddress';
import type ParcelTemplate from '~/types/ParcelTemplate';
import type User from '~/types/User';

import { type Currency } from '~/constants/currency';
import { type DistanceUnit } from '~/constants/distance';
import { WeightUnit } from '~/constants/weight';
import { type Promo } from '~/modules/billing/components/PromoCode/types/promo.type';
import { OnboardingLocalStorageKeys } from '~/modules/onboarding/types/onboarding.type';
import { type Activation, type UserDraftRequests } from '~/reducers/user/user.reducer';
import { getCurrentPlan } from '~/selectors/plans.selectors';
import { hasStoreConnected } from '~/selectors/stores.selectors';
import { isAddressIncomplete, type BaseAddress, type Country } from '~/types/Address';
import { AssetType, type Asset } from '~/types/Asset';
import { type CustomEntitlement, type QuantityEntitlement } from '~/types/Entitlements';
import LabelFormat, { DefaultLabelFormat } from '~/types/LabelFormat';
import { type UserPlan } from '~/types/Plan';
import {
  type ProfileUser,
  type UserConfigDefaults,
  type UserDefaultServiceLevel,
} from '~/types/User';
import { hasValidBilling, isHighFraudRisk } from '~/types/User';
import { type FeatureFlagUserData } from '~/utils/featureflags/types';
import { extractRefreshable } from '~/utils/fetchable';
import { responsiveCheck } from '~/utils/responsiveCheck';
import { isNotNil } from '~/utils/utils';
import { isPotentialFraudEmail, isShippoEmail } from '~/utils/validations';

import { type ApplicationState } from '../store';

export const creditCardConveienceFeeTokens = {
  CREDIT_CARD_CONVENIENCE_FEE: '3%',
  CREDIT_CARD_CONVENIENCE_FEE_1_5_PCT: '1.5%',
  CREDIT_CARD_CONVENIENCE_FEE_1_25_PCT: '1.25%',
  CREDIT_CARD_CONVENIENCE_FEE_1_75_PCT: '1.75%',
  CREDIT_CARD_CONVENIENCE_FEE_1_PCT: '1%',
  CREDIT_CARD_CONVENIENCE_FEE_2_5_PCT: '2.5%',
  CREDIT_CARD_CONVENIENCE_FEE_2_25_PCT: '2.25%',
  CREDIT_CARD_CONVENIENCE_FEE_2_75_PCT: '2.75%',
  CREDIT_CARD_CONVENIENCE_FEE_2_PCT: '2%',
  CREDIT_CARD_CONVENIENCE_FEE_3_5_PCT: '3.5%',
  CREDIT_CARD_CONVENIENCE_FEE_3_25_PCT: '3.25%',
};

export const achDiscountTokens = {
  ACH_DISCOUNT: '1%',
  ACH_DISCOUNT_0_5_PCT: '0.5%',
  ACH_DISCOUNT_0_75_PCT: '0.75%',
  ACH_DISCOUNT_1_5_PCT: '1.5%',
  ACH_DISCOUNT_1_25_PCT: '1.25%',
  ACH_DISCOUNT_1_75_PCT: '1.75%',
  ACH_DISCOUNT_2_PCT: '2%',
};

const getUserStore = (state: ApplicationState) => state.user;

export const getUser = createSelector(getUserStore, (userStore): Maybe<User> => userStore.user);

export const getUserIsLoading = createSelector(
  getUserStore,
  (userStore): boolean => userStore.isLoading,
);

export const getUserIsAuthenticated = createSelector(
  getUserStore,
  (userStore): boolean => userStore.isAuthenticated,
);

export const getUserId = createSelector(
  getUser,
  (user): Maybe<number> => (user && user.id) || null,
);

export const getUserObjectId = createSelector(
  getUser,
  (user): Maybe<string> => (user && user.objectId) || null,
);

export const getUserAreOrderTagsVisible = createSelector(
  getUser,
  (user): boolean => user?.areOrderTagsVisible || false,
);

export const getUserAddress = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<APIAddress> => (user ? user.address : null),
);

export const getUserReturnAddress = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<APIAddress> => (user ? user.addressReturn : null),
);

export const getCompanyName = createSelector(
  getUserStore,
  (userStore): Maybe<string> =>
    userStore.company
      ? userStore.company.companyName
        ? userStore.company.companyName
        : null
      : null,
);

export const getUserEmail = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.email : null),
);

export const getUserPhoneNumber = createSelector(
  getUserAddress,
  (address: Maybe<APIAddress>): string => (address && address.phone ? address.phone : ''),
);

const getUserCountry = createSelector(
  getUserAddress,
  (address: Maybe<APIAddress>): Maybe<Country> => (address ? address.country : null),
);

export const getUserCountryIso2 = createSelector(
  getUserCountry,
  (country: Maybe<Country>): Maybe<string> => (country ? country.iso2 : null),
);

export const getIsUserCountryUS = createSelector(
  getUserCountryIso2,
  (iso2: Maybe<string>): boolean => iso2 === 'US',
);

export const getIsUserCountryCA = createSelector(
  getUserCountryIso2,
  (iso2: Maybe<string>): boolean => iso2 === 'CA',
);

const getUserEntitlements = createSelector(
  getUserStore,
  (userStore) => userStore.entitlements || [],
);

export const getUserIsShippoStaff = createSelector(getUserEntitlements, (entitlements) =>
  entitlements.some(({ token }) => token === 'shippo_staff'),
);

export const getUserHasCustomBranding = createSelector(getUserEntitlements, (entitlements) =>
  entitlements.some(({ token }) => token === 'custom_branding'),
);

export const getUserUserSeats = createSelector(getUserEntitlements, (entitlements) => {
  const userSeatsEntitlement = entitlements.find(
    (entitlement): entitlement is QuantityEntitlement => entitlement.token === 'user_seats',
  );
  if (!userSeatsEntitlement) return 1;

  return userSeatsEntitlement.isUnlimited ? Infinity : userSeatsEntitlement.quantity;
});

export const getUserHasUserManagement = createSelector(
  getUserUserSeats,
  (userSeats) => userSeats > 1,
);

export const getUserSupportType = createSelector(getUserEntitlements, (entitlements): string => {
  const supportTypeEntitlement = entitlements.find(
    (entitlement): entitlement is CustomEntitlement => entitlement.token === 'support_type',
  );
  if (!supportTypeEntitlement) return 'BASIC';

  return supportTypeEntitlement.value;
});

export const getDefaultParcel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<ParcelTemplate> => (user ? user.defaultParcel : null),
);

export const getDefaultDistanceUnit = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<DistanceUnit> => (user ? user.defaultDistanceUnit : null),
);

export const getDefaultMassUnit = createSelector(
  getUser,
  (user: Maybe<User>): WeightUnit => (user ? user.defaultMassUnit : WeightUnit.LB),
);

const getDefaultCurrency = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<Currency> => (user ? user.currencyIso : null),
);

const getDefaultProvider = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultProvider : null),
);

const getDefaultServiceLevelAccountId = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user?.defaultOutboundAccount : null),
);

export const getDefaultServiceLevel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultServiceLevel : null),
);

const getDefaultReturnProvider = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultReturnProvider : null),
);

const getDefaultReturnProviderObjectId = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user?.defaultReturnAccount : null),
);

const getDefaultReturnServiceLevel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<string> => (user ? user.defaultReturnServiceLevel : null),
);

export const getDefaultLabelFormat = createSelector(getUser, (user: Maybe<User>): LabelFormat => {
  if (user && user.defaultLabelFiletype) return user.defaultLabelFiletype;

  return DefaultLabelFormat;
});

export const getHasDefaultMergedLabels = createSelector(
  getDefaultLabelFormat,
  (labelFormat): boolean =>
    [
      LabelFormat.PDF_W_PSLIP_8x11,
      LabelFormat.PDF_W_PSLIP_COLLATED_4x6,
      LabelFormat.PDF_W_PSLIP_COLLATED_4x8,
      LabelFormat.PDF_W_PSLIP_COLLATED_8x11,
    ].includes(labelFormat),
);

export const getDefaultAutoCreateReturnLabel = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<boolean> => (user ? user.defaultAutoCreateReturnLabel : null),
);

export const getCurrentPromo = createSelector(
  getUser,
  (user: Maybe<User>): Maybe<Promo> => (user ? user.currentPromo : null),
);

export const getUserConfigDefaults = createSelector(
  getUser,
  getDefaultParcel,
  getDefaultDistanceUnit,
  getDefaultMassUnit,
  getDefaultCurrency,
  getDefaultAutoCreateReturnLabel,
  (
    user,
    defaultParcel,
    defaultDistanceUnit,
    defaultMassUnit,
    defaultCurrency,
    defaultAutoCreateReturnLabel,
  ): Partial<UserConfigDefaults> =>
    user
      ? {
          defaultAutoCreateReturnLabel: defaultAutoCreateReturnLabel!,
          defaultCurrency: defaultCurrency!,
          defaultDistanceUnit: defaultDistanceUnit!,
          defaultMassUnit: defaultMassUnit!,
          defaultParcel: defaultParcel!,
        }
      : {},
);

export const getUserDefaultServiceLevel = createSelector(
  getUser,
  getDefaultProvider,
  getDefaultServiceLevel,
  (user, defaultProvider, defaultServiceLevel): Maybe<{ provider: string; token: string }> =>
    user && defaultProvider && defaultServiceLevel
      ? { provider: defaultProvider, token: defaultServiceLevel }
      : null,
);

export const getUserDefaultServiceLevelExtended = createSelector(
  getUser,
  getDefaultProvider,
  getDefaultServiceLevel,
  getDefaultServiceLevelAccountId,
  (
    user,
    defaultProviderName,
    defaultServiceLevelToken,
    defaultServiceLevelAccountId,
  ): UserDefaultServiceLevel | null =>
    user && defaultProviderName && defaultServiceLevelToken
      ? {
          accountId: defaultServiceLevelAccountId,
          provider: defaultProviderName,
          token: defaultServiceLevelToken,
        }
      : null,
);

export const getUserDefaultReturnServiceLevel = createSelector(
  getUser,
  getDefaultReturnProvider,
  getDefaultReturnServiceLevel,
  (
    user,
    defaultReturnProvider,
    defaultReturnServiceLevel,
  ): Maybe<{ provider: string; token: string }> =>
    user && defaultReturnProvider && defaultReturnServiceLevel
      ? { provider: defaultReturnProvider, token: defaultReturnServiceLevel }
      : null,
);

export const getUserDefaultReturnServiceLevelExtended = createSelector(
  getUser,
  getDefaultReturnProvider,
  getDefaultReturnServiceLevel,
  getDefaultReturnProviderObjectId,
  (
    user,
    defaultReturnProvider,
    defaultReturnServiceLevel,
    defaultReturnProviderObjectId,
  ): Maybe<{ objectId: null | string | undefined; provider: string; token: string }> =>
    user && defaultReturnProvider && defaultReturnServiceLevel
      ? {
          objectId: defaultReturnProviderObjectId,
          provider: defaultReturnProvider,
          token: defaultReturnServiceLevel,
        }
      : null,
);

export const getIsUserAddressValid = createSelector(
  getUserAddress,
  (address): boolean => address !== null && !isAddressIncomplete(address as BaseAddress),
);

export const getIsUserReturnAddressValid = createSelector(
  getUserReturnAddress,
  (address): boolean => address !== null && !isAddressIncomplete(address as BaseAddress),
);

export const getUserAddressIfValid = createSelector(
  getUserAddress,
  getIsUserAddressValid,
  (userAddress: Maybe<APIAddress>, valid: boolean): Maybe<APIAddress> =>
    valid ? userAddress : null,
);

export const getUserReturnAddressIfValid = createSelector(
  getUserReturnAddress,
  getIsUserReturnAddressValid,
  (userReturnAddress: Maybe<APIAddress>, valid: boolean): Maybe<APIAddress> =>
    valid ? userReturnAddress : null,
);

export const userIsBillingBlocked = createSelector(
  getUser,
  (user: Maybe<User>): boolean => isNotNil(user) && user.blockedBilling,
);

export const userHasValidBilling = createSelector(
  getUser,
  (user) => isNotNil(user) && hasValidBilling(user),
);

export const userIsHighFraudRisk = createSelector(
  getUser,
  (user) => isNotNil(user) && isHighFraudRisk(user),
);

export const userIsLabelDownloadBlocked = createSelector(
  getUser,
  (user): boolean =>
    isNotNil(user) &&
    isHighFraudRisk(user) &&
    moment().diff(user.dateJoined, 'days') < 15 &&
    !user.hasPaidInvoice,
);

export const showEmailVerificationModal = createSelector(
  getUserStore,
  (userStore) => userStore.showEmailVerificationModal,
);

export const getNextModalAction = createSelector(
  getUserStore,
  (userStore) => userStore.nextModalAction,
);

export const getRequiresEmailVerification = createSelector(getUser, (user): boolean =>
  user ? user.requiresEmailVerification : false,
);

export const getIsEligibleForUPSSavings = createSelector(
  getUser,
  (user): boolean =>
    !!user &&
    !!user.savingsCarrierName &&
    user.savingsCarrierName === 'UPS' &&
    !!user.savingsMonthlyAmount,
);

export const getSavingsMonthlyAmount = createSelector(getUser, (user) =>
  user && user.savingsMonthlyAmount ? user.savingsMonthlyAmount : 0,
);

export const getUserAssets = createSelector(
  getUserStore,
  (userStore): Refreshable<Asset[], string> => userStore.assets,
);

export const getUserAssetDrafts = createSelector(
  getUserStore,
  (userStore): UserDraftRequests => userStore.assetDrafts,
);

export const getIsIDVRequired = createSelector(getUser, (user): boolean => !!user?.isIDVRequired);

export const getUserLogoAsset = createSelector(getUserAssets, (refreshableAssets): Maybe<Asset> => {
  const assets = extractRefreshable<Asset[], string>(refreshableAssets);
  return assets ? assets.find((asset) => asset.type === AssetType.LOGO) || null : null;
});

type AssetsByType = {
  [Property in AssetType]: Map<number, Asset>;
};

export const getUserAssetsByType = createSelector(
  getUserAssets,
  (refreshableAssets): AssetsByType => {
    const assets = extractRefreshable<Asset[], string>(refreshableAssets);
    let assetsByType = <AssetsByType>{};
    Object.values(AssetType).forEach((assetType) => {
      assetsByType[assetType] = new Map<number, Asset>();
    });
    if (!assets) return assetsByType;

    assets.forEach((asset) => {
      if (asset && asset?.type && assetsByType[asset.type]) {
        const rank: number = asset?.rank || 1;
        assetsByType[asset.type].set(rank, asset);
      }
    });
    return assetsByType;
  },
);

// If the user's payment type is GOCARDLESS, use German Shippo address on invoice (copied from Ember)
export const getIsGermanShippoAddressInvoice = createSelector(
  getUser,
  (user): boolean => user?.paymentType === 'GOCARDLESS',
);

export const getHasManualPaymentMethod = createSelector(
  getUser,
  (user): boolean => user?.paymentType === 'MANUAL',
);

export const getHasPurchasedLabel = createSelector(
  getUser,
  (user): boolean | undefined => user?.hasPurchasedLabel,
);

export const getActivationData = createSelector(
  getUserStore,
  (userStore): Activation => userStore.activation,
);

export const getUserDataForFeatureTests = createSelector(
  getUser,
  getActivationData,
  getCurrentPlan,
  hasStoreConnected,
  (
    user: Maybe<User>,
    activationData: Activation,
    plan: Maybe<UserPlan>,
    hasStoreConnected: boolean,
  ): FeatureFlagUserData | null => {
    const isMobile = responsiveCheck();
    const userAgent = navigator.userAgent;

    if (!user) return null;

    const {
      address,
      currentPromo,
      dateJoined,
      email,
      firstName,
      hasPurchasedLabel,
      id,
      isActive,
      lastName,
      lifetimeVolSegmentMax,
      lifetimeVolSegmentMin,
      revenueTierName,
      signedUpAt,
    } = user;
    const dateJoinedParsed = dateJoined.valueOf();
    const signedUpAtParsed = signedUpAt * 1000;
    const country = address && address.country ? address?.country.name : undefined;
    const primaryCarrier = localStorage.getItem('primary_carrier') ?? '';
    const hasPurchasedFirstLabel = localStorage.getItem('has_purchased_first_label') ?? false;
    const userType =
      localStorage.getItem(OnboardingLocalStorageKeys.ONBOARDING_BUSINESS_TYPE) ?? '';
    const registrationStore = localStorage.getItem('registration_store') ?? '';
    const proPlanVariantCookie = Cookies.get('pro_plan_variant') ?? '';

    return {
      country: country || '',
      dateJoined: dateJoinedParsed,
      hasPromo: Boolean(currentPromo),
      hasPurchasedFirstLabel: Boolean(hasPurchasedFirstLabel),
      hasPurchasedLabel,
      hasStoreConnected,
      id,
      isActive,
      isE2EAutomatedTestUser:
        isShippoEmail(email) && (email.startsWith('test+') || email.startsWith('stester')),
      isE2EGetStartedUser: isShippoEmail(email) && email.startsWith('test-get-started+'),
      isE2ETestUser:
        (isShippoEmail(email) && email.startsWith('test+')) ||
        (firstName === 'Test' && lastName === 'Cafe'),
      isMobile,
      isPotentialFraudEmail: isPotentialFraudEmail(email),
      isShippoEmail: isShippoEmail(email),
      lifetimeVolSegmentMax,
      lifetimeVolSegmentMin,
      ordersCount: <number>activationData.ordersCount,
      plan: plan ? plan.plan.name : '',
      primaryCarrier,
      proPlanVariant: proPlanVariantCookie,
      registrationStore,
      revenueTierName,
      signedUpAt: signedUpAtParsed,
      userAgent,
      userId: id,
      userType,
    };
  },
);

export const getUserProfile = createSelector(
  getUser,
  (user) =>
    (user
      ? {
          email: user.email,
          federatedEmails: user.federatedEmails,
          firstName: user.firstName,
          isAccountOwner: user.isAccountOwner,
          isPasswordLoginAllowed: user.isPasswordLoginAllowed,
          lastName: user.lastName,
          loginUserId: user.loginUserId,
          name: `${user.firstName} ${user.lastName}`,
        }
      : null) as ProfileUser,
);

export const getShouldShowPromoApplicationNotice = createSelector(
  getUserStore,
  (user): boolean => user.showPromoApplicationNotice,
);

export const getUserMetadataForSegment = createSelector(
  getUser,
  (user: Maybe<User>): Partial<User> | null => {
    if (user) {
      return pick(user, [
        'id',
        'loginUserId',
        'email',
        'username',
        'firstName',
        'lastName',
        'address',
        'revenueTierName',
        'blockedBilling',
        'fraudStatus',
        'paymentType',
        'currentPromo',
        'dateJoined',
        'lifetimeVolSegmentMin',
        'lifetimeVolSegmentMax',
        'isIDVRequired',
        'isAccountOwner',
      ]);
    } else {
      return null;
    }
  },
);

export const getUserInsuranceRateDomestic = createSelector(
  getUser,
  (user): number => user?.insuranceRateDomestic || 0.0125,
);

export const getUserInsuranceRateInternaional = createSelector(
  getUser,
  (user): number => user?.insuranceRateInternational || 0.015,
);

export const getCompanySettings = createSelector(getUserStore, (userStore) => userStore.company);

export const getBrandColors = createSelector(getCompanySettings, (company) => [
  company?.brandPrimaryColor,
  company?.brandSecondaryColor,
]);

export const getCompanyIsLoading = createSelector(
  getUserStore,
  (userStore): boolean => userStore.isLoadingCompany,
);

export const getIsThirdPartyInsuranceDisabled = createSelector(
  getUser,
  (user): boolean => !!user?.isThirdPartyInsuranceDisabled,
);

export const getCreditCardConvenienceFeeToken = createSelector(
  getUser,
  (user): string | undefined =>
    user?.invoiceAdjustments.find((adjustment) => !!creditCardConveienceFeeTokens[adjustment]),
);

export const getAchDiscountToken = createSelector(
  getUser,
  (user): string | undefined =>
    user?.invoiceAdjustments.find((adjustment) => !!achDiscountTokens[adjustment]),
);

export const getDaysSinceJoined = createSelector(getUser, (user) => {
  if (!user) return;

  const { dateJoined } = user;
  return differenceInDays(Date.now(), dateJoined.toDate());
});

export const getIsPrepaidBilling = createSelector(getUser, (user) => user?.isPrepaidBilling);
export const getAvailableCredit = createSelector(getUser, (user) => user?.availableCredit || []);
